import { ApiError } from '@/api/http/types'
import {
  INSTRUMENT_INFO_START_NETWORK_ERROR,
  INSTRUMENT_UNAVAILABLE,
  SURVEY_SESSION_TIMED_OUT,
  SURVEY_SESSION_TIMED_OUT_SUBMIT,
} from './instrument-info-messages'

export const SURVEY_SAVE_GENERIC_ERROR = INSTRUMENT_INFO_START_NETWORK_ERROR

/** TC-BE-23 / TC-BE-11 — handler routing from execution handlers. */
export type SurveySessionBlockedReason = 'timeout' | 'target'

export type SurveySaveFailureAction =
  | { kind: 'inline_required' }
  | { kind: 'toast'; message: string }
  | { kind: 'session_target_blocked' }
  | { kind: 'session_timeout'; message: string }
  | { kind: 'instrument_paused'; message: string }

function resolveApiFailure(err: unknown, options?: { submit?: boolean }): SurveySaveFailureAction {
  if (err instanceof ApiError) {
    if (err.code === 'answer_required') {
      return { kind: 'inline_required' }
    }
    if (err.code === 'instrument_paused' || err.code === 'instrument_unavailable') {
      return {
        kind: 'instrument_paused',
        message:
          err.code === 'instrument_unavailable'
            ? INSTRUMENT_UNAVAILABLE
            : err.message.trim() || INSTRUMENT_UNAVAILABLE,
      }
    }
    if (err.code === 'capacity_reached' || err.code === 'target_reached') {
      return { kind: 'session_target_blocked' }
    }
    if (err.code === 'session_timeout') {
      return {
        kind: 'session_timeout',
        message: options?.submit ? SURVEY_SESSION_TIMED_OUT_SUBMIT : SURVEY_SESSION_TIMED_OUT,
      }
    }
  }
  return { kind: 'toast', message: SURVEY_SAVE_GENERIC_ERROR }
}

export function sessionTargetBlockedFromResponse(input: {
  status?: string
  terminationReason?: string | null
  instrumentTargetReached?: boolean
}): boolean {
  return (
    input.instrumentTargetReached === true ||
    input.status === 'abandoned_target_reached' ||
    input.terminationReason === 'abandoned_target_reached'
  )
}

export function sessionTimeoutFromResponse(input: {
  status?: string
  sessionExpired?: boolean
  terminationReason?: string | null
}): boolean {
  if (sessionTargetBlockedFromResponse(input)) return false
  return (
    Boolean(input.sessionExpired) ||
    input.status === 'abandoned_session_timeout' ||
    input.status === 'abandoned_browser_closed' ||
    input.status === 'abandoned_app_closed'
  )
}

/** Maps save-answer API failures — never surfaces raw stack traces or server messages in UI. */
export function resolveSurveySaveFailure(err: unknown): SurveySaveFailureAction {
  return resolveApiFailure(err)
}

/** Maps submit API failures (TC-BE-24, TC-BE-28, TC-BE-23). */
export function resolveSurveySubmitFailure(err: unknown): SurveySaveFailureAction {
  return resolveApiFailure(err, { submit: true })
}
